import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import "./Login.css";
import snapgradeLogo from '../../../img/snapgrade-logo.png';
import { API_ENDPOINTS } from '../../../config/api.config';
import { postData } from '../../../utils/apiUtils';
import axios from 'axios';
import { getApiUrl } from '../../../utils/apiUtils';
import { getAuthHeaders } from '../../../utils/apiUtils';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState("password"); // "password" or "magic-link"
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [generatedSchoolCode, setGeneratedSchoolCode] = useState(null);
  const [schoolName, setSchoolName] = useState(null);
  const [isResetPasswordMode, setIsResetPasswordMode] = useState(false);
  const [resetPasswordSent, setResetPasswordSent] = useState(false);

  useEffect(() => {
    // Check for school code in localStorage (from registration process)
    const storedSchoolCode = localStorage.getItem('generatedSchoolCode');
    const storedSchoolName = localStorage.getItem('trialSchoolName');
    
    if (storedSchoolCode) {
      setGeneratedSchoolCode(storedSchoolCode);
      setSchoolName(storedSchoolName || "SnapGrade Trial School");
      
      // Keep the school code visible longer, but still remove eventually
      // so it doesn't show up on subsequent normal logins
      setTimeout(() => {
        localStorage.removeItem('generatedSchoolCode');
        localStorage.removeItem('trialSchoolName');
      }, 300000); // 5 minutes (300,000 ms)
    }

    if (location.state?.error) {
      setError(location.state.error);
    }
    if (location.state?.success) {
      setSuccessMessage(location.state.success);
    }
  }, [location.state]);

  const formatSchoolCode = (code) => {
    if (!code || typeof code !== 'string' || code.length !== 16) return code;
    return `${code.substring(0, 4)}-${code.substring(4, 8)}-${code.substring(8, 12)}-${code.substring(12, 16)}`;
  };

  const copyCodeToClipboard = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        setSuccessMessage("School code copied to clipboard");
        setTimeout(() => {
          if (successMessage === "School code copied to clipboard") {
            setSuccessMessage("");
          }
        }, 3000);
      })
      .catch(err => {
        console.error("Failed to copy code:", err);
        setError("Failed to copy code. Please try selecting and copying manually.");
      });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Convert email to lowercase
    const submissionData = {
      ...formData,
      email: formData.email.toLowerCase()
    };
    
    try {
      // Handle password reset request
      if (isResetPasswordMode) {
        const response = await postData(API_ENDPOINTS.RESET_PASSWORD.REQUEST, { email: submissionData.email });
        
        if (response.success) {
          setResetPasswordSent(true);
          setSuccessMessage("Password reset instructions sent! Please check your email.");
        } else {
          throw new Error(response.message || 'Failed to send password reset instructions.');
        }
      }
      // Use different endpoints based on login method
      else if (loginMethod === 'magic-link') {
        // For magic link, we only need to send the email
        const response = await postData(API_ENDPOINTS.MAGIC_LINK.REQUEST, { email: submissionData.email });
        
        if (response.success) {
          setMagicLinkSent(true);
          setSuccessMessage("Magic link sent! Please check your email.");
        } else {
          throw new Error(response.message || 'Failed to send magic link.');
        }
      } else {
        // Standard password login
        const response = await postData(API_ENDPOINTS.LOGIN, submissionData);
        
        if (response && response.token) {
          // Store tokens
          localStorage.setItem("token", response.token);
          localStorage.setItem("refreshToken", response.refreshToken);
          
          // Create user data with additional fields for permissions
          const userData = {
            name: response.message ? response.message.split(", ")[1] : 'User',
            role: response.role,
            userId: response.userId,
            school: response.school?.name,
            schoolCode: response.school?.code,
            // Add these fields to support the payments section
            schoolType: response.role === 'teacher' ? 'personal' : 
                      response.role === 'admin' ? 'classroom' : null,
            permissions: ['view_payments', 'manage_payments']
          };
          
          // Store user data
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem("userName", userData.name);
          localStorage.setItem("role", userData.role);
          
          // Identify user in PostHog
          if (posthog) {
            posthog.identify(userData.userId, {
              email: submissionData.email,
              name: userData.name,
              role: userData.role,
              school: userData.school,
              schoolCode: userData.schoolCode,
              loginMethod: 'password'
            });
          }
          
          // Trigger a storage event for other components
          window.dispatchEvent(new Event('storage'));
          
          // Navigate based on role
          switch(response.role.toLowerCase()) {
            case 'admin':
              navigate("/admin-dashboard");
              break;
            default:
              navigate("/dashboard");
          }
        } else {
          setError(response?.message || 'Login failed. Please try again.');
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Use the friendlyMessage property if available
      if (error.friendlyMessage) {
        setError(error.friendlyMessage);
      } else if (error.response) {
        if (error.response.status === 401) {
          // Don't show "Invalid credentials" for password reset
          if (isResetPasswordMode) {
            setError('Unable to process password reset. Please check your email address.');
          } else {
            setError('Invalid credentials. Please check your email and password.');
          }
        } else if (error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError('Login failed. Please try again.');
        }
      } else if (error.request) {
        // The request was made but no response was received
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        // Default fallback error message
        setError(error?.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const switchLoginMethod = (method) => {
    setLoginMethod(method);
    setError("");
    setSuccessMessage("");
    setMagicLinkSent(false);
    setIsResetPasswordMode(false);
    setResetPasswordSent(false);
  };

  const toggleResetPasswordMode = () => {
    setIsResetPasswordMode(!isResetPasswordMode);
    setError("");
    setSuccessMessage("");
    setResetPasswordSent(false);
  };

  return (
    <div className="login-container">
      <div className="left-section">
        <img 
          src={snapgradeLogo} 
          alt="SnapGrade Logo" 
          className="logo" 
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
      </div>
      <div className="right-section">
        <div className="login-form-container">
          <h1>{isResetPasswordMode ? 'Reset Your Password' : 'Welcome back'}</h1>
          <p className="subtitle">
            {isResetPasswordMode 
              ? 'Enter your email to receive password reset instructions' 
              : 'Enter your credentials to access your account'}
          </p>
          
          {generatedSchoolCode && !isResetPasswordMode && (
            <div className="school-code-display">
              <h3>Your School Code</h3>
              <p>Keep this code to connect additional accounts to your school.</p>
              <div className="school-code-value">
                <span>{formatSchoolCode(generatedSchoolCode)}</span>
                <button 
                  type="button" 
                  className="copy-button"
                  onClick={() => copyCodeToClipboard(generatedSchoolCode)}
                >
                  Copy
                </button>
              </div>
              {schoolName && (
                <p className="school-name-display">School: {schoolName}</p>
              )}
              <p className="code-timing-note">This code will remain visible for 5 minutes. You can use this code to connect additional accounts to your school. We will send you an email with the code and you can view it again in Settings.</p>
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}
          
          {!isResetPasswordMode && (
            <div className="login-method-tabs">
              <button 
                className={`login-method-tab ${loginMethod === 'password' ? 'active' : ''}`}
                onClick={() => switchLoginMethod('password')}
              >
                Password
              </button>
              <button 
                className={`login-method-tab ${loginMethod === 'magic-link' ? 'active' : ''}`}
                onClick={() => switchLoginMethod('magic-link')}
              >
                Magic Link
              </button>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            
            {loginMethod === 'password' && !isResetPasswordMode && (
              <div className="form-group">
                <div className="password-header">
                  <label>Password</label>
                  <button 
                    type="button" 
                    className="forgot-password"
                    onClick={toggleResetPasswordMode}
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>
            )}

            {loginMethod === 'magic-link' && !isResetPasswordMode && magicLinkSent ? (
              <div className="magic-link-info">
                <p>Please check your email for the magic link. The link will expire in 15 minutes.</p>
                <button 
                  type="button" 
                  className="resend-button"
                  onClick={() => setMagicLinkSent(false)}
                >
                  Didn't receive it? Send again
                </button>
              </div>
            ) : isResetPasswordMode && resetPasswordSent ? (
              <div className="reset-password-info">
                <p>Please check your email for password reset instructions.</p>
                <button 
                  type="button" 
                  className="resend-button"
                  onClick={() => setResetPasswordSent(false)}
                >
                  Didn't receive it? Send again
                </button>
              </div>
            ) : (
              <button 
                type="submit" 
                className="sign-in-button" 
                disabled={isLoading}
              >
                {isLoading 
                  ? "Processing..." 
                  : isResetPasswordMode
                    ? "Send Reset Instructions"
                    : loginMethod === 'password' 
                      ? "Sign in" 
                      : "Send Magic Link"}
              </button>
            )}

            {isResetPasswordMode ? (
              <p className="signup-prompt">
                <button 
                  type="button" 
                  className="back-to-login-link"
                  onClick={toggleResetPasswordMode}
                >
                  Back to login
                </button>
              </p>
            ) : (
              <p className="signup-prompt">
                Don't have an account? <Link to="/register" className="signup-link">Sign up</Link>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
